import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  FilePlus2, FolderPlus, LifeBuoy, Clock, Plus, ChevronDown, ScrollText, Archive, ArchiveRestore, AlertCircle,
} from 'lucide-react';
import type { CustomerHubSummary, EngagementType } from '../../types';
import OverflowMenu, { type OverflowMenuItem } from '../details/OverflowMenu';
import MenuButton from '../table/MenuButton';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../services/api';
import { canOpenTicket } from '../../utils/ticketOrigin';
import { WORK_TYPE_ORDER } from './CustomerWorkTypes';

interface Action {
  key: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}

/**
 * Context-aware action center (decision 7), rendered as the Customer Hub
 * header actions. Every action is driven by the hub summary: it appears only
 * when it applies to the customer's current state and the viewer is
 * permitted to perform it — never as a dead button. An archived customer
 * offers only "restore".
 *
 * P-01/C2 (D-17) — the order is fixed by the product decision:
 *   1. "+ הוספת התקשרות ▾" (primary): project / development / service
 *      contract / hour bank / לל"ח → the existing engagement form with that
 *      type chosen and this customer locked;
 *   2. "+ איש קשר"  3. "העלאת מסמך"  4. "פתיחת קריאה";
 *   5. "⋯": edit, create contract, new quote, create folder, log time, archive, hard delete.
 * Each action keeps the permission it had before (write link; contacts are
 * an admin surface; archive / hard delete are the General Manager's). On a
 * phone the secondary buttons move into "⋯".
 */
export default function ActionCenter({
  orgId,
  summary,
  summaryLoading,
  summaryError,
  canAddContact,
  onAddEngagement,
  onAddContact,
  onCreateContract,
  onArchive,
  onRestore,
  extraMenuItems = [],
}: {
  orgId: string;
  summary: CustomerHubSummary | undefined;
  summaryLoading: boolean;
  summaryError: boolean;
  /** Contacts are an admin surface (same audience as the contacts tab). */
  canAddContact: boolean;
  onAddEngagement: (type: EngagementType) => void;
  onAddContact: () => void;
  /** From an accepted quote when there is one, otherwise the plain contract form. */
  onCreateContract: () => void;
  onArchive: () => void;
  onRestore: () => void;
  /** Page-level menu items: edit (first) and hard delete (danger, last). */
  extraMenuItems?: OverflowMenuItem[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // D-309 s.5: "new folder" also for the PM / team lead of an active project (the server's documents write rule).
  // Same query (and cache) as the documents explorer's top level.
  const docsWriteQuery = useQuery({
    queryKey: ['documentsWriteAccess', orgId, null],
    queryFn: () => api.getDocumentsWriteAccess(orgId, { folderId: null }),
    enabled: !!summary && !summary.permissions.can_write && !!summary.organization.active,
    staleTime: 60_000,
  });

  if (summaryLoading) {
    return (
      <div className="flex items-center gap-2" aria-busy="true">
        <span className="sr-only">{t('organization.actions.loading')}</span>
        <div className="skeleton h-9 w-32 rounded-lg" />
        <div className="skeleton h-9 w-9 rounded-lg" />
      </div>
    );
  }
  // No summary and no error: not asked for this viewer (a customer login, whose
  // customer-hub API is refused) — no actions to offer, and no error to show.
  if (!summary && !summaryError) {
    return extraMenuItems.length > 0 ? <OverflowMenu label={t('uxDetails.moreActions')} items={extraMenuItems} /> : null;
  }
  if (summaryError || !summary) {
    return (
      <div className="flex items-center gap-2">
        <p className="flex items-center gap-1.5 text-sm text-danger-700" role="alert">
          <AlertCircle size={16} aria-hidden="true" /> {t('organization.actions.loadError')}
        </p>
        {extraMenuItems.length > 0 && <OverflowMenu label={t('uxDetails.moreActions')} items={extraMenuItems} />}
      </div>
    );
  }

  const { permissions, organization } = summary;
  const archived = !organization.active;
  const editItems = extraMenuItems.filter((it) => !it.danger);
  const dangerItems = extraMenuItems.filter((it) => it.danger);

  if (archived) {
    const menu = [...editItems, ...dangerItems];
    return (
      <div className="flex flex-wrap items-center gap-2">
        {/* DQ-134 (D-30 exception, D-313): "הוספת התקשרות" from the customer's own page stays available on an archived customer and returns it to active (D-17). */}
        {permissions.can_write && (
          <MenuButton
            label={t('organization.addEngagement')}
            align="start"
            popoverClassName="w-56 p-1.5"
            triggerClassName="btn btn-secondary"
            items={WORK_TYPE_ORDER.map((ty) => ({
              key: ty,
              label: ty === 'service_without_contract' ? t('organization.tabLll') : t(`engagementDetail.types.${ty}`),
              onSelect: () => onAddEngagement(ty),
            }))}
          >
            <Plus size={16} aria-hidden="true" /> {t('organization.addEngagement')}
            <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
          </MenuButton>
        )}
        {permissions.is_general_manager && (
          <button type="button" onClick={onRestore} className="btn btn-primary">
            <ArchiveRestore size={16} aria-hidden="true" /> {t('organization.actions.restore')}
          </button>
        )}
        {menu.length > 0 && <OverflowMenu label={t('uxDetails.moreActions')} items={menu} />}
      </div>
    );
  }

  const secondary: Action[] = [];
  const menuMiddle: OverflowMenuItem[] = [];
  if (permissions.can_write) {
    if (canAddContact) {
      secondary.push({ key: 'contact', label: t('organization.actions.addContact'), icon: Plus, onClick: onAddContact });
    }
    // P-45L (D-210): an employee opens no ticket - the customer does, in the portal.
    if (canOpenTicket(user)) {
      secondary.push({
        key: 'ticket', label: t('organization.actions.openServiceRequest'), icon: LifeBuoy,
        onClick: () => navigate(`/tickets?create=true&organization_id=${orgId}`),
      });
    }
    menuMiddle.push(
      {
        key: 'contract', label: t('organization.actions.createContract'), icon: <ScrollText size={16} />, onSelect: onCreateContract,
      },
      {
        key: 'quote', label: t('organization.actions.newQuote'), icon: <FilePlus2 size={16} />,
        onSelect: () => navigate(`/quotes?create=1&organization_id=${orgId}`),
      },
      {
        // QA-R1 #4 — the documents page's existing "new folder" dialog, opened
        // on this customer's folders (same permissions as creating it there).
        key: 'folder', label: t('organization.actions.createFolder'), icon: <FolderPlus size={16} />,
        onSelect: () => navigate(`/documents?org=${orgId}&createFolder=1`),
      },
      {
        key: 'time', label: t('organization.actions.logTime'), icon: <Clock size={16} />,
        onSelect: () => navigate('/work-logs?create=true'),
      },
    );
  }
  if (!permissions.can_write && docsWriteQuery.data?.data?.can_write === true) {
    menuMiddle.push({
      key: 'folder', label: t('organization.actions.createFolder'), icon: <FolderPlus size={16} />,
      onSelect: () => navigate(`/documents?org=${orgId}&createFolder=1`),
    });
  }
  const archiveItems: OverflowMenuItem[] = permissions.is_general_manager
    ? [{ key: 'archive', label: t('organization.actions.archive'), icon: <Archive size={16} />, onSelect: onArchive }]
    : [];

  const menuItems: OverflowMenuItem[] = [
    // On a phone the secondary buttons live here (they are visible from sm up).
    ...secondary.map((a) => ({ key: a.key, label: a.label, icon: <a.icon size={16} />, onSelect: a.onClick, className: 'sm:hidden' })),
    ...editItems,
    ...menuMiddle,
    ...archiveItems,
    ...dangerItems,
  ];

  if (!permissions.can_write && menuItems.length === 0) {
    return <p className="text-xs text-surface-600">{t('organization.actions.none')}</p>;
  }

  const renderSecondary = (a: Action): ReactNode => {
    const Icon = a.icon;
    return (
      <button key={a.key} type="button" onClick={a.onClick} className="btn btn-secondary hidden sm:inline-flex">
        <Icon size={16} aria-hidden="true" /> {a.label}
      </button>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {permissions.can_write && (
        <MenuButton
          label={t('organization.addEngagement')}
          align="start"
          popoverClassName="w-56 p-1.5"
          triggerClassName="btn btn-primary"
          items={WORK_TYPE_ORDER.map((ty) => ({
            key: ty,
            label: ty === 'service_without_contract' ? t('organization.tabLll') : t(`engagementDetail.types.${ty}`),
            onSelect: () => onAddEngagement(ty),
          }))}
        >
          <Plus size={16} aria-hidden="true" /> {t('organization.addEngagement')}
          <ChevronDown size={16} strokeWidth={1.75} aria-hidden="true" />
        </MenuButton>
      )}
      {secondary.map(renderSecondary)}
      {menuItems.length > 0 && <OverflowMenu label={t('uxDetails.moreActions')} items={menuItems} />}
    </div>
  );
}

/**
 * What needs attention for this customer ("2 open quotes · 1 open request"),
 * shown as a quiet line in the header. Nothing is rendered for an archived
 * customer or while the summary is unavailable.
 */
export function HubAttention({ summary }: { summary: CustomerHubSummary | undefined }) {
  const { t } = useTranslation();
  if (!summary || !summary.organization.active) return null;
  const { counts } = summary;
  const items: string[] = [];
  if (counts.open_quotes > 0) items.push(t('organization.actions.openQuotes', { count: counts.open_quotes }));
  if (counts.draft_contracts > 0) items.push(t('organization.actions.draftContracts', { count: counts.draft_contracts }));
  if (counts.open_service_requests > 0) items.push(t('organization.actions.openRequests', { count: counts.open_service_requests }));
  if (items.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-surface-600">
      <span className="w-1.5 h-1.5 rounded-full bg-warning-500" aria-hidden="true" />
      {items.map((txt, i) => (
        <span key={i}>{i > 0 && <span className="me-2 text-surface-400" aria-hidden="true">·</span>}{txt}</span>
      ))}
    </p>
  );
}
